import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import React, { useEffect, useRef, useState, useCallback } from "react";
import Loading from "../Loading/Loading";

/* ─── Magnetic letter hook ───────────────────────────────────
   Letters in the headline softly attract toward the cursor
   when it's within a radius, then spring back.
─────────────────────────────────────────────────────────── */
function useMagneticLetters(ref, active) {
  useEffect(() => {
    if (!active || !ref.current) return;
    const spans = Array.from(ref.current.children);
    const MAG_R = 120;
    const MAG_STR = 0.18;

    // Cache rects once — recompute on resize only
    let rects = spans.map(s => s.getBoundingClientRect());
    const refreshRects = () => { rects = spans.map(s => s.getBoundingClientRect()); };
    window.addEventListener("resize", refreshRects, { passive: true });

    // Throttle via rAF — store latest mouse coords, process on next frame
    let pending = false;
    let mx = 0, my = 0;

    const process = () => {
      pending = false;
      spans.forEach((span, idx) => {
        const r = rects[idx];
        const cx = r.left + r.width / 2;
        const cy = r.top + r.height / 2;
        const dx = mx - cx;
        const dy = my - cy;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < MAG_R) {
          const pull = (1 - dist / MAG_R) ** 2;
          gsap.to(span, { x: dx * pull * MAG_STR, y: dy * pull * MAG_STR, duration: 0.35, ease: "power2.out" });
        } else {
          gsap.to(span, { x: 0, y: 0, duration: 0.6, ease: "elastic.out(1, 0.4)" });
        }
      });
    };

    const onMove = (e) => {
      mx = e.clientX; my = e.clientY;
      if (!pending) { pending = true; requestAnimationFrame(process); }
    };

    const onLeave = () => {
      spans.forEach(span => gsap.to(span, { x: 0, y: 0, duration: 0.8, ease: "elastic.out(1, 0.35)" }));
    };

    window.addEventListener("mousemove", onMove);
    ref.current.addEventListener("mouseleave", onLeave);
    const el = ref.current;
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("resize", refreshRects);
      el.removeEventListener("mouseleave", onLeave);
      spans.forEach(span => gsap.set(span, { x: 0, y: 0 }));
    };
  }, [active, ref]);
}

gsap.registerPlugin(ScrollTrigger);

/* ─── Grid constants ─────────────────────────────────────── */
const CELL = 48;
const BULGE_R = 160;
const BULGE_MAX = 22;
const SHATTER_R = 80;
const SHATTER_DUR = 900;
const GRID_TILT = 6;

/* ─── Cursor trail constants ─────────────────────────────── */
const INK_FADE = 900;        // ms trail lingers
const INK_VEL_SCALE = 0.12;  // velocity → intensity
const TRAIL_R = 88;          // base influence radius
const TRAIL_STREAK = 2.6;    // directional elongation factor

/* ─── Scramble role tag ──────────────────────────────────── */
const ROLES = ["Fullstack Developer", "Frontend Engineer", "UI Craftsman", "Backend Architect"];
const CHARS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$";

function ScrambleRole() {
  const [display, setDisplay] = useState(ROLES[0]);
  const [roleIdx, setRoleIdx] = useState(0);
  const intervalRef = useRef(null);
  const timeoutRef = useRef(null);

  useEffect(() => {
    const cycle = () => {
      const nextIdx = (roleIdx + 1) % ROLES.length;
      const target = ROLES[nextIdx];
      let iter = 0;
      const totalFrames = target.length * 3;
      clearInterval(intervalRef.current);
      intervalRef.current = setInterval(() => {
        setDisplay(
          target.split("").map((ch, i) => {
            if (i < Math.floor(iter / 3)) return ch;
            return ch === " " ? " " : CHARS[Math.floor(Math.random() * CHARS.length)];
          }).join("")
        );
        iter++;
        if (iter >= totalFrames) {
          clearInterval(intervalRef.current);
          setDisplay(target);
          setRoleIdx(nextIdx);
        }
      }, 35);
    };
    timeoutRef.current = setTimeout(cycle, 2200);
    return () => { clearTimeout(timeoutRef.current); clearInterval(intervalRef.current); };
  }, [roleIdx]);

  return (
    <span
      className="font-Montreal-Mono text-xs md:text-sm tracking-[0.18em] uppercase select-none"
      style={{ color: "#9E9A90", minWidth: "22ch", display: "inline-block" }}
    >
      [ {display} ]
    </span>
  );
}

/* ─── Mobile city grid ───────────────────────────────────── */
function MobileCityGrid({ wrapperRef }) {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const W = window.innerWidth;
    const H = Math.round(window.innerHeight * 0.52);
    canvas.width = W * dpr;
    canvas.height = H * dpr;
    canvas.style.width = W + "px";
    canvas.style.height = H + "px";
    const ctx = canvas.getContext("2d");
    ctx.scale(dpr, dpr);

    // ── buildings ──
    const COLS = 18;
    const colW = W / COLS;
    const buildings = Array.from({ length: COLS }, (_, i) => {
      const h = H * (0.28 + Math.random() * 0.48);
      const w = colW * (0.55 + Math.random() * 0.3);
      const gap = (colW - w) / 2;
      const winCols = Math.floor(w / 9);
      const winRows = Math.floor(h / 12);
      const windows = Array.from({ length: winCols * winRows }, () => ({
        lit: Math.random() > 0.42,
        flicker: Math.random() > 0.82,
        phase: Math.random() * Math.PI * 2,
        speed: 0.3 + Math.random() * 1.0,
        nextToggle: 2000 + Math.random() * 8000,
        lastToggle: 0,
      }));
      const hasAntenna = Math.random() > 0.6;
      return {
        x: i * colW + gap, y: H - h, w, h,
        winCols, winRows, windows, hasAntenna,
        antennaPhase: Math.random() * Math.PI * 2,
      };
    });

    // ── shooting stars pool ──
    const stars = [];
    let nextStarTime = 1200 + Math.random() * 3000;

    const spawnStar = () => {
      const fromLeft = Math.random() > 0.5;
      stars.push({
        x: fromLeft ? -20 : W + 20,
        y: H * (0.04 + Math.random() * 0.28),
        vx: fromLeft ? 2.8 + Math.random() * 2 : -(2.8 + Math.random() * 2),
        vy: 0.4 + Math.random() * 0.6,
        len: 40 + Math.random() * 60,
        born: performance.now(),
        duration: 900 + Math.random() * 600,
      });
    };

    // ── moon ──
    const moonX = W * 0.72;
    const moonY = H * 0.18;
    const moonR = 11;

    // ── parallax drift ──
    let driftT = 0;

    const draw = (ts) => {
      driftT += 0.0008;
      const drift = Math.sin(driftT) * 1.8;

      ctx.clearRect(0, 0, W, H);

      // moon
      ctx.save();
      ctx.globalAlpha = 0.18;
      ctx.beginPath();
      ctx.arc(moonX, moonY, moonR, 0, Math.PI * 2);
      ctx.fillStyle = "#F0EDE8";
      ctx.fill();
      ctx.globalCompositeOperation = "destination-out";
      ctx.beginPath();
      ctx.arc(moonX + moonR * 0.42, moonY - moonR * 0.1, moonR * 0.82, 0, Math.PI * 2);
      ctx.fill();
      ctx.globalCompositeOperation = "source-over";
      ctx.restore();

      // shooting stars
      const now = performance.now();
      if (now > nextStarTime) { spawnStar(); nextStarTime = now + 2500 + Math.random() * 5000; }
      for (let i = stars.length - 1; i >= 0; i--) {
        const s = stars[i];
        const progress = (now - s.born) / s.duration;
        if (progress >= 1) { stars.splice(i, 1); continue; }
        const a = progress < 0.2 ? progress / 0.2 : progress > 0.75 ? 1 - (progress - 0.75) / 0.25 : 1;
        s.x += s.vx * 0.016 * 60;
        s.y += s.vy * 0.016 * 60;
        const tailX = s.x - (s.vx / Math.abs(s.vx)) * s.len;
        const tailY = s.y - s.vy * (s.len / Math.abs(s.vx));
        const sg = ctx.createLinearGradient(tailX, tailY, s.x, s.y);
        sg.addColorStop(0, "rgba(240,237,232,0)");
        sg.addColorStop(1, `rgba(212,169,106,${a * 0.9})`);
        ctx.beginPath(); ctx.moveTo(tailX, tailY); ctx.lineTo(s.x, s.y);
        ctx.strokeStyle = sg; ctx.lineWidth = 1.2; ctx.stroke();
        ctx.beginPath(); ctx.arc(s.x, s.y, 1.4, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,230,140,${a * 0.95})`; ctx.fill();
      }

      // buildings
      buildings.forEach(b => {
        const bx = b.x + drift;
        ctx.fillStyle = "rgba(14,13,11,0.94)";
        ctx.fillRect(bx, b.y, b.w, b.h);
        ctx.strokeStyle = "rgba(245,244,240,0.05)";
        ctx.lineWidth = 0.5;
        ctx.strokeRect(bx, b.y, b.w, b.h);

        const winW = b.w / b.winCols;
        const winH = b.h / b.winRows;
        b.windows.forEach((win, idx) => {
          if (ts - win.lastToggle > win.nextToggle) {
            win.lit = !win.lit;
            win.lastToggle = ts;
            win.nextToggle = 3000 + Math.random() * 9000;
          }
          if (!win.lit) return;
          const col = idx % b.winCols;
          const row = Math.floor(idx / b.winCols);
          const wx = bx + col * winW + winW * 0.18;
          const wy = b.y + row * winH + winH * 0.18;
          const ww = winW * 0.64;
          const wh = winH * 0.64;
          let alpha = 0.52;
          if (win.flicker) alpha = 0.22 + 0.32 * (0.5 + 0.5 * Math.sin(ts * 0.001 * win.speed + win.phase));
          const g = ctx.createRadialGradient(wx + ww/2, wy + wh/2, 0, wx + ww/2, wy + wh/2, ww * 1.2);
          g.addColorStop(0, `rgba(255,218,120,${alpha})`);
          g.addColorStop(1, `rgba(212,169,106,${alpha * 0.2})`);
          ctx.fillStyle = g;
          ctx.fillRect(wx, wy, ww, wh);
        });

        if (b.hasAntenna) {
          const ax = bx + b.w / 2;
          ctx.strokeStyle = "rgba(245,244,240,0.15)";
          ctx.lineWidth = 0.8;
          ctx.beginPath(); ctx.moveTo(ax, b.y); ctx.lineTo(ax, b.y - 10); ctx.stroke();
          const blinkOn = Math.sin(ts * 0.0018 + b.antennaPhase) > 0.3;
          if (blinkOn) {
            ctx.beginPath(); ctx.arc(ax, b.y - 10, 1.8, 0, Math.PI * 2);
            ctx.fillStyle = "rgba(255,80,80,0.85)"; ctx.fill();
            const rg = ctx.createRadialGradient(ax, b.y - 10, 0, ax, b.y - 10, 6);
            rg.addColorStop(0, "rgba(255,80,80,0.25)"); rg.addColorStop(1, "rgba(255,80,80,0)");
            ctx.fillStyle = rg;
            ctx.beginPath(); ctx.arc(ax, b.y - 10, 6, 0, Math.PI * 2); ctx.fill();
          }
        }
      });

      ctx.strokeStyle = "rgba(212,169,106,0.1)";
      ctx.lineWidth = 0.75;
      ctx.beginPath(); ctx.moveTo(0, H - 1); ctx.lineTo(W, H - 1); ctx.stroke();

      rafRef.current = requestAnimationFrame(draw);
    };

    rafRef.current = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  return (
    <div
      ref={wrapperRef}
      aria-hidden
      className="absolute bottom-0 left-0 right-0 md:hidden pointer-events-none"
      style={{ zIndex: 0, height: "52%" }}
    >
      {/* noise overlay */}
      <svg style={{ position: "absolute", inset: 0, width: "100%", height: "100%", opacity: 0.07, pointerEvents: "none" }}>
        <filter id="city-noise">
          <feTurbulence type="fractalNoise" baseFrequency="0.72" numOctaves="3" stitchTiles="stitch" />
          <feColorMatrix type="saturate" values="0" />
        </filter>
        <rect width="100%" height="100%" filter="url(#city-noise)" />
      </svg>

      <canvas ref={canvasRef} style={{ display: "block", opacity: 0.38 }} />

      {/* gradient fade — bottom blends into bg, top fades into hero */}
      <div style={{
        position: "absolute", inset: 0, pointerEvents: "none",
        background: "linear-gradient(to bottom, #0A0A08 0%, transparent 22%, transparent 60%, #0A0A08 100%)",
      }} />
    </div>
  );
}

/* ─────────────────────────────────────────────────────────── */

export default function Hero({ lenis }) {
  const heroRef = useRef(null);
  const canvasRef = useRef(null);
  const headline1 = useRef(null);
  const headline2 = useRef(null);
  const headlineWrap = useRef(null);  /* wraps both h1s — shard target */
  const metaRef = useRef(null);
  const descRef = useRef(null);
  const scrollHintRef = useRef(null);
  const scrollLineRef = useRef(null);
  const availableDotRef = useRef(null);
  const availableDotMobileRef = useRef(null);
  const mobileStatusRef = useRef(null);
  const mobileDescRef = useRef(null);
  const mobileHeadlineRef = useRef(null);
  const mobileCityRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [localTime, setLocalTime] = useState("");
  const [dotHovered, setDotHovered] = useState(false);

  const mouseRef = useRef({ x: -9999, y: -9999 });
  const mousePrevRef = useRef({ x: -9999, y: -9999 });
  const mouseVelRef = useRef(0);
  const inkDrops = useRef([]);   // { lx, ly, opacity, born }
  const shards = useRef([]);
  const raf = useRef(null);

  const isMobile = window.innerWidth <= 768;
  const isLowEnd = navigator.hardwareConcurrency <= 4 || window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  /* ── lock scroll while loading ── */
  useEffect(() => {
    if (isLoading) { lenis.stop(); document.body.style.overflow = "hidden"; }
    else { document.body.style.overflow = ""; }
  }, [isLoading, lenis]);

  /* ── canvas grid renderer ── */
  const initCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const hero = heroRef.current;
    if (!canvas || !hero || isMobile) return;

    const dpr = Math.min(window.devicePixelRatio || 1, isLowEnd ? 1 : 2);
    let W = hero.offsetWidth, H = hero.offsetHeight;
    canvas.width = W * dpr; canvas.height = H * dpr;
    canvas.style.width = W + "px"; canvas.style.height = H + "px";
    const ctx = canvas.getContext("2d");
    ctx.scale(dpr, dpr);

    const resize = () => {
      W = hero.offsetWidth; H = hero.offsetHeight;
      canvas.width = W * dpr; canvas.height = H * dpr;
      canvas.style.width = W + "px"; canvas.style.height = H + "px";
      ctx.scale(dpr, dpr);
    };
    window.addEventListener("resize", resize);

    const cols = Math.ceil(W / CELL) + 1;
    const rows = Math.ceil(H / CELL) + 1;

    let canvasPaused = true; // start paused; observer kick-starts the loop
    const visObserver = new IntersectionObserver(([entry]) => {
      const wasVisible = !canvasPaused;
      canvasPaused = !entry.isIntersecting;
      // start loop on first visibility, or when re-entering viewport after a pause
      if (!canvasPaused && !wasVisible) raf.current = requestAnimationFrame(draw);
    }, { rootMargin: "200px" });
    visObserver.observe(hero);

    const draw = () => {
      if (canvasPaused) {
        raf.current = null;
        return;
      }
      ctx.clearRect(0, 0, W, H);

      const { x: mx, y: my } = mouseRef.current;
      const rect = canvas.getBoundingClientRect();
      const lx = mx - rect.left;
      const ly = my - rect.top;

      // ── cursor trail: lit cell borders ──
      const now = Date.now();
      inkDrops.current = inkDrops.current.filter(d => now - d.born < INK_FADE);
      inkDrops.current.forEach(drop => {
        const t = (now - drop.born) / INK_FADE;
        // ease-out fade: bright hit, long tail
        const fade = (1 - t) ** 1.8;
        const intensity = drop.intensity * fade;
        if (intensity < 0.01) return;

        // direction unit vector (streak axis)
        const { vx, vy, speed } = drop;
        const hasDir = speed > 1;

        const scan = Math.ceil(TRAIL_R / CELL) + 1;
        const cCenter = Math.round(drop.lx / CELL);
        const rCenter = Math.round(drop.ly / CELL);

        for (let dc = -scan; dc <= scan; dc++) {
          for (let dr = -scan; dr <= scan; dr++) {
            const c = cCenter + dc;
            const r = rCenter + dr;
            if (c < 0 || c >= cols || r < 0 || r >= rows) continue;

            const cx = c * CELL + CELL / 2;
            const cy = r * CELL + CELL / 2;
            let dx = cx - drop.lx;
            let dy = cy - drop.ly;

            // squash the space along movement direction → elongate trail
            if (hasDir) {
              const along = dx * vx + dy * vy;   // projection onto velocity
              const perpX = dx - along * vx;
              const perpY = dy - along * vy;
              // compress perpendicular distance (makes ellipse wider along motion)
              dx = perpX + along * vx / TRAIL_STREAK;
              dy = perpY + along * vy / TRAIL_STREAK;
            }

            const dist = Math.sqrt(dx * dx + dy * dy);
            if (dist >= TRAIL_R) continue;

            const norm = (1 - dist / TRAIL_R) ** 2;
            const cellIntensity = intensity * norm;
            if (cellIntensity < 0.012) continue;

            const pad = 3;
            const x = c * CELL + pad;
            const y = r * CELL + pad;
            const sz = CELL - pad * 2;
            const radius = 3;

            // inner fill — very faint warm tint
            ctx.fillStyle = `rgba(255,248,220,${(cellIntensity * 0.06).toFixed(3)})`;
            ctx.beginPath();
            ctx.roundRect(x, y, sz, sz, radius);
            ctx.fill();

            // border stroke — warm cream, sharp at center, hairline at edges
            const strokeAlpha = cellIntensity * 0.55;
            ctx.strokeStyle = `rgba(220,200,160,${strokeAlpha.toFixed(3)})`;
            ctx.lineWidth = norm > 0.6 ? 0.9 : 0.45;
            ctx.beginPath();
            ctx.roundRect(x, y, sz, sz, radius);
            ctx.stroke();

            // hot-center dot at intersection point
            if (norm > 0.72) {
              const dotAlpha = (norm - 0.72) / 0.28 * intensity * 0.7;
              ctx.fillStyle = `rgba(255,230,140,${dotAlpha.toFixed(3)})`;
              ctx.beginPath();
              ctx.arc(c * CELL, r * CELL, 1.5, 0, Math.PI * 2);
              ctx.fill();
            }
          }
        }
      });

      ctx.strokeStyle = "rgba(245,244,240,0.07)";
      ctx.lineWidth = 0.5;

      for (let c = 0; c < cols; c++) {
        ctx.beginPath();
        for (let r = 0; r < rows; r++) {
          let px = c * CELL, py = r * CELL;
          const dx = px - lx, dy = py - ly;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < BULGE_R && dist > 0) {
            const s = (1 - dist / BULGE_R) ** 2;
            px += (dx / dist) * BULGE_MAX * s;
            py += (dy / dist) * BULGE_MAX * s;
          }
          shards.current.forEach(s => {
            const sdx = c * CELL - s.ox, sdy = r * CELL - s.oy;
            const sd = Math.sqrt(sdx * sdx + sdy * sdy);
            if (sd < s.r * 2) {
              const t = Math.min((Date.now() - s.born) / SHATTER_DUR, 1);
              const e = t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
              const frac = (1 - e) * (1 - sd / (s.r * 2));
              px += s.vx * frac * 30; py += s.vy * frac * 30;
            }
          });
          r === 0 ? ctx.moveTo(px, py) : ctx.lineTo(px, py);
        }
        ctx.stroke();
      }

      for (let r = 0; r < rows; r++) {
        ctx.beginPath();
        for (let c = 0; c < cols; c++) {
          let px = c * CELL, py = r * CELL;
          const dx = px - lx, dy = py - ly;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < BULGE_R && dist > 0) {
            const s = (1 - dist / BULGE_R) ** 2;
            px += (dx / dist) * BULGE_MAX * s;
            py += (dy / dist) * BULGE_MAX * s;
          }
          shards.current.forEach(s => {
            const sdx = c * CELL - s.ox, sdy = r * CELL - s.oy;
            const sd = Math.sqrt(sdx * sdx + sdy * sdy);
            if (sd < s.r * 2) {
              const t = Math.min((Date.now() - s.born) / SHATTER_DUR, 1);
              const e = t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
              const frac = (1 - e) * (1 - sd / (s.r * 2));
              px += s.vx * frac * 30; py += s.vy * frac * 30;
            }
          });
          c === 0 ? ctx.moveTo(px, py) : ctx.lineTo(px, py);
        }
        ctx.stroke();
      }

      shards.current = shards.current.filter(s => Date.now() - s.born < SHATTER_DUR);
      raf.current = requestAnimationFrame(draw);
    };
    // IntersectionObserver starts the loop when hero enters view

    return () => { cancelAnimationFrame(raf.current); visObserver.disconnect(); window.removeEventListener("resize", resize); };
  }, [isMobile, isLowEnd]);

  /* ── mouse tracking (for grid bulge) ── */
  useEffect(() => {
    const hero = heroRef.current;
    if (!hero || isMobile) return;

    const onMove = (e) => {
      const prev = mouseRef.current;
      const dx = e.clientX - prev.x;
      const dy = e.clientY - prev.y;
      const speed = Math.sqrt(dx * dx + dy * dy);
      mousePrevRef.current = { ...prev };
      mouseVelRef.current = speed;
      mouseRef.current = { x: e.clientX, y: e.clientY };

      // spawn a trail drop — throttle to ~every 2px of movement
      const intensity = Math.min(speed * INK_VEL_SCALE, 1);
      if (intensity > 0.04) {
        const rect = hero.getBoundingClientRect();
        const vx = speed > 0 ? dx / speed : 0;
        const vy = speed > 0 ? dy / speed : 0;
        inkDrops.current.push({
          lx: e.clientX - rect.left,
          ly: e.clientY - rect.top,
          intensity,
          vx, vy, speed,
          born: Date.now(),
        });
        // cap trail length to keep perf tight
        if (inkDrops.current.length > (isLowEnd ? 30 : 60)) inkDrops.current.shift();
      }
    };
    const onClick = (e) => {
      const rect = hero.getBoundingClientRect();
      const lx = e.clientX - rect.left, ly = e.clientY - rect.top;
      for (let i = 0; i < 8; i++) {
        const angle = (i / 8) * Math.PI * 2;
        shards.current.push({ ox: lx, oy: ly, vx: Math.cos(angle), vy: Math.sin(angle), r: SHATTER_R, born: Date.now() });
      }
    };

    hero.addEventListener("mousemove", onMove);
    hero.addEventListener("click", onClick);
    return () => { hero.removeEventListener("mousemove", onMove); hero.removeEventListener("click", onClick); };
  }, [isMobile, isLowEnd]);

  /* ── load complete → animate in ── */
  const handleLoadComplete = () => {
    setIsLoading(false);
    lenis.start();
    setTimeout(animateIn, 80);
    initCanvas();
  };

  const animateIn = () => {
    [headline2].forEach(ref => {
      if (!ref.current) return;
      ref.current.style.opacity = "1";
    });

    const tl = gsap.timeline();
    tl.fromTo(headline1.current,
      { y: 90, opacity: 0, rotationX: -75 },
      { y: 0, opacity: 1, rotationX: 0, duration: 1, ease: "power4.out" }
    );
    if (headline2.current) {
      tl.fromTo(headline2.current,
        { y: 90, opacity: 0, rotationX: -75 },
        { y: 0, opacity: 1, rotationX: 0, duration: 1, ease: "power4.out" },
        "-=0.75"
      );
    }
    if (mobileHeadlineRef.current) {
      tl.fromTo(mobileHeadlineRef.current,
        { y: 60, opacity: 0 },
        { y: 0, opacity: 1, duration: 1.1, ease: "power4.out" },
        "<"
      );
    }
    tl.fromTo(
      [descRef.current, mobileDescRef.current].filter(Boolean),
      { opacity: 0, y: 20 },
      { opacity: 1, y: 0, duration: 0.7, ease: "power3.out" },
      "-=0.5"
    );
    tl.fromTo(
      [metaRef.current, scrollHintRef.current, mobileStatusRef.current].filter(Boolean),
      { opacity: 0, y: 28 },
      { opacity: 1, y: 0, duration: 0.8, stagger: 0.12, ease: "power3.out" },
      "-=0.4"
    );
  };

  /* ── mousemove parallax on headlines ── */
  useEffect(() => {
    if (isMobile) return;
    const hero = heroRef.current;
    if (!hero) return;
    const onMove = (e) => {
      const rect = hero.getBoundingClientRect();
      const nx = (e.clientX - rect.left) / rect.width - 0.5;   // -0.5 … 0.5
      const ny = (e.clientY - rect.top) / rect.height - 0.5;
      if (headline1.current) gsap.to(headline1.current, { x: nx * 18, y: ny * 10, duration: 0.9, ease: "power2.out" });
      if (headline2.current) gsap.to(headline2.current, { x: nx * -24, y: ny * -14, duration: 0.9, ease: "power2.out" });
    };
    hero.addEventListener("mousemove", onMove);
    return () => hero.removeEventListener("mousemove", onMove);
  }, [isMobile]);

  /* ── scroll-hint line hover grow ── */
  useEffect(() => {
    const hint = scrollHintRef.current;
    const line = scrollLineRef.current;
    if (!hint || !line) return;
    const onEnter = () => gsap.to(line, { scaleY: 2, transformOrigin: "top center", duration: 0.4, ease: "power2.out" });
    const onLeave = () => gsap.to(line, { scaleY: 1, transformOrigin: "top center", duration: 0.6, ease: "elastic.out(1, 0.4)" });
    hint.addEventListener("mouseenter", onEnter);
    hint.addEventListener("mouseleave", onLeave);
    return () => { hint.removeEventListener("mouseenter", onEnter); hint.removeEventListener("mouseleave", onLeave); };
  }, []);

  /* ── availability dot pulse on load ── */
  useEffect(() => {
    if (isLoading) return;
    [availableDotRef, availableDotMobileRef].forEach(ref => {
      if (!ref.current) return;
      gsap.fromTo(ref.current,
        { scale: 0, opacity: 0 },
        { scale: 1, opacity: 1, duration: 0.5, ease: "back.out(2)", delay: 1.2 }
      );
    });
  }, [isLoading]);

  /* ── live local time ticker (only when dot is hovered) ── */
  useEffect(() => {
    if (!dotHovered) return;
    const fmt = () => new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
    setLocalTime(fmt());
    const id = setInterval(() => setLocalTime(fmt()), 1000);
    return () => clearInterval(id);
  }, [dotHovered]);

  /* ── scroll parallax ── */
  useEffect(() => {
    const ctx = gsap.context(() => {
      const trigger = { trigger: heroRef.current, start: "top top", end: "bottom top", scrub: true };
      // whole section
      gsap.to(heroRef.current, { yPercent: -6, ease: "none", scrollTrigger: trigger });
      // mobile layers — each at a different depth
      if (mobileHeadlineRef.current)
        gsap.to(mobileHeadlineRef.current, { yPercent: -8, ease: "none", scrollTrigger: trigger });
      if (mobileDescRef.current)
        gsap.to(mobileDescRef.current, { yPercent: -14, ease: "none", scrollTrigger: trigger });
      if (mobileCityRef.current)
        gsap.to(mobileCityRef.current, { yPercent: -22, ease: "none", scrollTrigger: trigger });
    });
    return () => ctx.revert();
  }, []);

  const month = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const now = new Date();
  const dateStr = `${month[now.getMonth()]} '${String(now.getFullYear()).slice(-2)}`;

  return (
    <>
      <Loading isLoading={isLoading} onLoadComplete={handleLoadComplete} />

      <section
        id="hero"
        ref={heroRef}
        className="relative full-viewport-height w-full flex flex-col justify-between px-5 md:px-10 pt-24 md:pt-28 pb-8 md:pb-10 overflow-hidden"
      >
        {/* ── blur vignette edges ── */}
        {/* top */}
        <div aria-hidden style={{
          position: "absolute", top: 0, left: 0, right: 0, height: "260px", zIndex: 1, pointerEvents: "none",
          backdropFilter: "blur(9px)",
          WebkitBackdropFilter: "blur(9px)",
          maskImage: "linear-gradient(to bottom, black 0%, black 20%, transparent 100%)",
          WebkitMaskImage: "linear-gradient(to bottom, black 0%, black 20%, transparent 100%)",
        }} />
        {/* left */}
        <div aria-hidden style={{
          position: "absolute", top: 0, bottom: 0, left: 0, width: "240px", zIndex: 1, pointerEvents: "none",
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)",
          maskImage: "linear-gradient(to right, black 0%, black 15%, transparent 100%)",
          WebkitMaskImage: "linear-gradient(to right, black 0%, black 15%, transparent 100%)",
        }} />
        {/* right */}
        <div aria-hidden style={{
          position: "absolute", top: 0, bottom: 0, right: 0, width: "240px", zIndex: 1, pointerEvents: "none",
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)",
          maskImage: "linear-gradient(to left, black 0%, black 15%, transparent 100%)",
          WebkitMaskImage: "linear-gradient(to left, black 0%, black 15%, transparent 100%)",
        }} />
        {/* bottom blur — tall, bleeds outside the section */}
        <div aria-hidden style={{
          position: "absolute", bottom: "-80px", left: 0, right: 0, height: "320px", zIndex: 1, pointerEvents: "none",
          backdropFilter: "blur(15px)",
          WebkitBackdropFilter: "blur(15px)",
          maskImage: "linear-gradient(to top, black 0%, black 25%, transparent 100%)",
          WebkitMaskImage: "linear-gradient(to top, black 0%, black 25%, transparent 100%)",
        }} />
        {/* radial dark gradient */}
        <div aria-hidden style={{
          position: "absolute", inset: 0, zIndex: 1, pointerEvents: "none",
          background: "radial-gradient(ellipse 70% 60% at 50% 50%, transparent 30%, rgba(0,0,0,0.55) 100%)",
        }} />
        {/* bottom fade-to-bg — seamless blend into next section */}
        <div aria-hidden style={{
          position: "absolute", bottom: 0, left: 0, right: 0, height: "180px", zIndex: 2, pointerEvents: "none",
          background: "linear-gradient(to top, #0A0A08 0%, transparent 100%)",
        }} />

        {/* ── SVG noise texture ── */}
        <svg aria-hidden style={{ position: "absolute", inset: 0, width: "100%", height: "100%", zIndex: 0, pointerEvents: "none", opacity: 0.045 }}>
          <filter id="hero-noise">
            <feTurbulence type="fractalNoise" baseFrequency="0.65" numOctaves="3" stitchTiles="stitch" />
            <feColorMatrix type="saturate" values="0" />
          </filter>
          <rect width="100%" height="100%" filter="url(#hero-noise)" />
        </svg>

        {/* ── grid canvas (tilted) — desktop only ── */}
        <canvas
          ref={canvasRef}
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none hidden md:block"
          style={{
            zIndex: 0,
            opacity: 0.55,
            transform: `perspective(900px) rotateX(${GRID_TILT}deg)`,
            transformOrigin: "center 60%",
          }}
        />

        {/* ── city grid canvas — mobile only ── */}
        <MobileCityGrid wrapperRef={mobileCityRef} />


        {/* ── content ── */}
        <div className="relative z-10 flex-1 flex flex-col justify-center">

          {/* ── MOBILE layout — centered, Rebeqa serif, full-screen presence ── */}
          <div className="md:hidden flex flex-col items-center text-center">

            {/* name — Rebeqa, massive, italic, centered */}
            <div className="overflow-hidden w-full">
              <h1
                ref={mobileHeadlineRef}
                className="opacity-0 select-none w-full text-center"
                style={{
                  fontFamily: "Rebeqa, serif",
                  fontSize: "clamp(5.5rem, 28vw, 28vw)",
                  fontWeight: 400,
                  fontStyle: "italic",
                  lineHeight: 0.88,
                  letterSpacing: "-0.01em",
                  color: "#F0EDE8",
                }}
              >
                Anees
                <span style={{ color: "#D4A96A" }}>.</span>
              </h1>
            </div>

            {/* role tag — mono, centered, below name */}
            <div ref={mobileDescRef} className="opacity-0 mt-5 flex flex-col items-center gap-4 w-full">
              <div className="flex items-center gap-3">
                <div style={{ width: 24, height: "1px", background: "#D4A96A", opacity: 0.5 }} />
                <span className="font-Montreal-Mono text-[9px] tracking-[0.35em] uppercase" style={{ color: "#9E9A90" }}>
                  Fullstack Developer
                </span>
                <div style={{ width: 24, height: "1px", background: "#D4A96A", opacity: 0.5 }} />
              </div>

              {/* bio */}
              <p className="font-Montreal-Regular leading-[1.7] max-w-[260px]" style={{ fontSize: "0.83rem", color: "rgba(245,244,240,0.45)" }}>
                Senior Fullstack Developer · 8+ years. I architect and ship complete products, clean APIs, solid data models, interfaces that hold up.
              </p>

              {/* stats row — centered, minimal */}
              {/* <div className="flex items-center gap-6 mt-2">
                <div className="flex flex-col items-center gap-[3px]">
                  <span className="font-Cabinet font-black leading-none" style={{ fontSize: "1.9rem", color: "#F0EDE8", letterSpacing: "-0.03em" }}>
                    8<span style={{ color: "#D4A96A" }}>+</span>
                  </span>
                  <span className="font-Montreal-Mono text-[7px] tracking-[0.25em] uppercase" style={{ color: "#9E9A90" }}>years</span>
                </div>
                <div style={{ width: "1px", height: 28, background: "rgba(245,244,240,0.08)" }} />
                <div className="flex flex-col items-center gap-[3px]">
                  <span className="font-Cabinet font-black leading-none" style={{ fontSize: "1.9rem", color: "#F0EDE8", letterSpacing: "-0.03em" }}>
                    100<span style={{ color: "#D4A96A" }}>+</span>
                  </span>
                  <span className="font-Montreal-Mono text-[7px] tracking-[0.25em] uppercase" style={{ color: "#9E9A90" }}>projects</span>
                </div>
              </div> */}
            </div>
          </div>

          {/* ── DESKTOP layout — original left-aligned ── */}
          <div className="hidden md:block w-full">
            <div ref={headlineWrap} className="w-full">
              <div className="overflow-hidden pb-3">
                <h1
                  ref={headline2}
                  className="font-Playfair uppercase opacity-0 select-none w-full"
                  style={{
                    fontSize: "clamp(3.8rem, 18.5vw, 18.5vw)",
                    fontWeight: 900,
                    lineHeight: 0.85,
                    letterSpacing: "-0.02em",
                    perspective: "820px",
                    color: "#F0EDE8",
                  }}
                >
                  Anees
                  <span style={{ color: "#D4A96A" }}>.</span>
                </h1>
              </div>
            </div>
            <div ref={descRef} className="opacity-0 mt-8 ml-auto max-w-sm w-full">
              <div className="flex flex-col gap-3">
                <div className="w-full h-px" style={{ background: "rgba(245,244,240,0.1)" }} />
                <p className="font-Montreal-Regular text-base leading-relaxed" style={{ color: "rgba(245,244,240,0.45)" }}>
                  Senior Fullstack Developer · 8+ years. I architect and ship complete products, clean APIs, solid data models, interfaces that hold up.
                </p>
              </div>
            </div>
          </div>

        </div>

        {/* ── bottom bar ── */}
        <div ref={metaRef} className="relative z-10 flex items-end justify-between opacity-0">
          {/* desktop bottom-left */}
          <div className="hidden md:flex flex-col gap-1">
            <div
              className="flex items-center gap-2 cursor-default"
              onMouseEnter={() => setDotHovered(true)}
              onMouseLeave={() => setDotHovered(false)}
            >
              <span ref={availableDotRef} className="relative flex items-center justify-center" style={{ width: 10, height: 10 }}>
                <span className="absolute inline-flex rounded-full bg-amber-400 opacity-0"
                  style={{ width: 10, height: 10, animation: "dot-ping 2s ease-out infinite" }} />
                <span className="absolute inline-flex rounded-full bg-amber-400 opacity-0"
                  style={{ width: 10, height: 10, animation: "dot-ping 2s ease-out 0.7s infinite" }} />
                <span className="relative inline-flex rounded-full bg-amber-400" style={{ width: 7, height: 7 }} />
              </span>
              <span className="font-Montreal-Mono text-[11px] tracking-widest uppercase transition-all duration-300"
                style={{ color: "#9E9A90" }}>
                {dotHovered && localTime ? localTime : "Available for work"}
              </span>
            </div>
            <span
              className="font-Cabinet leading-none"
              style={{ fontSize: "clamp(2rem,7vw,5.5rem)", fontWeight: 900, color: "#F0EDE8" }}
            >
              {dateStr}
            </span>
          </div>

          {/* mobile bottom — available above date, coords + chevron right */}
          <div className="md:hidden w-full flex items-end justify-between">
            <div className="flex flex-col gap-[3px]">
              {/* available for work — above date */}
              <div className="flex items-center gap-[6px]">
                <span ref={availableDotMobileRef} className="relative flex items-center justify-center" style={{ width: 7, height: 7 }}>
                  <span className="absolute inline-flex rounded-full bg-amber-400 opacity-0"
                    style={{ width: 7, height: 7, animation: "dot-ping 2s ease-out infinite" }} />
                  <span className="absolute inline-flex rounded-full bg-amber-400 opacity-0"
                    style={{ width: 7, height: 7, animation: "dot-ping 2s ease-out 0.7s infinite" }} />
                  <span className="relative inline-flex rounded-full bg-amber-400" style={{ width: 5, height: 5 }} />
                </span>
                <span className="font-Montreal-Mono text-[9px] tracking-[0.28em] uppercase" style={{ color: "#9E9A90" }}>
                  Available for work
                </span>
              </div>
              {/* date — large typographic anchor */}
              <span
                className="font-Cabinet leading-none"
                style={{ fontSize: "clamp(2rem, 10vw, 4rem)", fontWeight: 900, color: "#F0EDE8", letterSpacing: "-0.03em" }}
              >
                {dateStr}
              </span>
            </div>
            <div className="flex flex-col items-end gap-2 pb-1">
              <span className="font-Montreal-Mono text-[9px] tracking-[0.16em]" style={{ color: "rgba(245,244,240,0.16)" }}>
                24.86°N 67.01°E
              </span>
              <div className="scroll-bounce flex flex-col items-center gap-[3px]">
                <div className="w-px h-6 opacity-20" style={{ background: "#F5F4F0" }} />
                <svg width="10" height="6" viewBox="0 0 10 6" fill="none">
                  <path d="M1 1L5 5L9 1" stroke="#F5F4F0" strokeOpacity="0.4" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              </div>
            </div>
          </div>

          {/* desktop scroll hint */}
          <div ref={scrollHintRef} className="hidden md:flex flex-col items-end gap-2 opacity-0">
            <span className="font-Montreal-Mono text-[10px] tracking-[0.22em] uppercase"
              style={{ color: "#9E9A90" }}>
              Scroll to explore
            </span>
            <div className="scroll-bounce flex flex-col items-center gap-[3px]">
              <div className="w-px h-8 bg-smoke opacity-20" />
              <svg width="10" height="6" viewBox="0 0 10 6" fill="none">
                <path d="M1 1L5 5L9 1" stroke="#F5F4F0" strokeOpacity="0.4" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </div>
          </div>
        </div>

      </section>
    </>
  );
}
